'use client';

import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logInstallPromptClicked from '@alltrails/amplitude/events/logInstallPromptClicked';
import logWebHeaderLoginButtonTapped from '@alltrails/amplitude/events/logWebHeaderLoginButtonTapped';
import useUser from '@alltrails/context/hooks/useUser';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import useAnimateInOutComponent from '@alltrails/core/hooks/useAnimateInOutComponent';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useScreenLock from '@alltrails/core/hooks/useScreenLock';
import Accordion from '@alltrails/denali/components/Accordion';
import Button from '@alltrails/denali/components/Button';
import { DropdownMenuItem } from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import Logo from '@alltrails/denali/components/Logo';
import Close from '@alltrails/denali/icons/Close';
import Menu from '@alltrails/denali/icons/Menu';
import Mobile from '@alltrails/denali/icons/Mobile';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import useDeeplink from '@alltrails/install-prompt/hooks/useDeeplink';
import { getHeaderLinkOnClickWithAnalytics, getUserLinks } from './headerLinks';
import styles from './styles/styles.module.scss';
import useHeaderLinks from './useHeaderLinks';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const MobileMenu = () => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const experiments = useExperiments();
  const sendHeaderLoginSignUpModalExposure = useExposureEvent('web-growth-rm-header-login-signup-modal');
  const isLoginSignUpModalTreatment = experiments['web-growth-rm-header-login-signup-modal']?.value === 'treatment';

  const [isOpen, setIsOpen] = useState(false);
  const [isAccountSectionOpen, setIsAccountSectionOpen] = useState(false);
  const [isLoginSignUpModalOpen, setIsLoginSignUpModalOpen] = useState(false);

  const getDeeplink = useDeeplink({ isOpen });

  const { isAnimating, isVisible, onAnimationEnd } = useAnimateInOutComponent(isOpen);

  useScreenLock(isVisible);

  const { headerLinks, getLinkInteractivity, signUpModal } = useHeaderLinks();

  const sections = headerLinks.slice(0);

  if (user) {
    const userLinks = getUserLinks({ user, languageRegionCode, includeLeadingIcons: true });
    sections.push({
      id: 'account',
      label: <FormattedMessage defaultMessage="Account" />,
      links: userLinks,
      webHeaderSection: 'my account'
    });
  }

  return (
    <>
      <IconButton
        className={styles.mobileIconButton}
        icon={{ Component: Menu }}
        onClick={() => setIsOpen(true)}
        title={intl.formatMessage({ defaultMessage: 'Navigation Menu' })}
        variant="flat"
      />
      {(isAnimating || isVisible) && (
        <ModalPortal>
          <div
            className={classNames(styles.mobileDrawerBackground, {
              [styles.isAnimating]: isAnimating,
              [styles.isVisible]: isVisible
            })}
            onAnimationEnd={onAnimationEnd}
          >
            <FocusTrap
              active
              focusTrapOptions={{
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: () => setIsOpen(false),
                fallbackFocus: '#mobile-header-drawer'
              }}
            >
              <div
                className={classNames(styles.mobileDrawer, {
                  [styles.isAnimating]: isAnimating,
                  [styles.isVisible]: isVisible
                })}
                id="mobile-header-drawer"
              >
                <div>
                  <div className={styles.drawerHeader}>
                    <Logo color="colored-dark" size="sm" variant="logomark" />
                    <IconButton
                      className={styles.mobileIconButton}
                      icon={{ Component: Close }}
                      onClick={() => {
                        setIsOpen(false);
                        setIsAccountSectionOpen(false);
                      }}
                      title={intl.formatMessage({ defaultMessage: 'Close' })}
                      variant="flat"
                    />
                  </div>
                  <Button
                    className={styles.drawerContinueInApp}
                    icon={{ Component: Mobile }}
                    linkInfo={{ href: getDeeplink() }}
                    onClick={() => {
                      logInstallPromptClicked({
                        install_prompt_location: 'mobile menu',
                        install_prompt_trigger: 'mobile menu',
                        install_prompt_type: 'banner'
                      });
                    }}
                    text={<FormattedMessage defaultMessage="Continue in app" />}
                    variant="primary"
                    width="full"
                  />
                  {sections.map((section, index) => {
                    return (
                      <Accordion
                        key={section.id}
                        borderBottomContainerClassName={index === sections.length - 1 ? styles.accordionNoBorder : undefined}
                        headingTypography="heading400"
                        initiallyOpen={index === 0}
                        title={section.label}
                        variant="minimal"
                        {...(section.id === 'account' && {
                          externalControls: {
                            isOpen: isAccountSectionOpen,
                            toggleIsOpen: () => setIsAccountSectionOpen(!isAccountSectionOpen)
                          }
                        })}
                      >
                        <div className={styles.accordionContents}>
                          {section.links.map((link, linkIndex) => {
                            const { linkInfo, onClick } = getLinkInteractivity(link);
                            const onClickWithAnalytics = getHeaderLinkOnClickWithAnalytics(
                              linkIndex,
                              link.webHeaderDropDownLink,
                              section.webHeaderSection,
                              () => {
                                setIsOpen(false);
                                onClick?.();
                              }
                            );
                            return (
                              <DropdownMenuItem
                                key={`${section.id}-${linkIndex}`}
                                option={{
                                  className: classNames(styles.mobileMenuItem, link.className),
                                  leadingIcon: link.leadingIcon,
                                  linkInfo,
                                  onClick: onClickWithAnalytics,
                                  subtext: link.subtext,
                                  title: link.title,
                                  trailingIcon: link.trailingIcon
                                }}
                                textVariant="text200"
                              />
                            );
                          })}
                        </div>
                      </Accordion>
                    );
                  })}
                </div>
                {user ? null : (
                  <div className={styles.drawerCTAs}>
                    <Button linkInfo={{ href: '/signup' }} text={<FormattedMessage defaultMessage="Sign up" />} variant="secondary" width="full" />
                    <Button
                      {...(isLoginSignUpModalTreatment ? {} : { linkInfo: { href: '/login' } })}
                      onClick={() => {
                        sendHeaderLoginSignUpModalExposure();
                        logWebHeaderLoginButtonTapped();
                        if (isLoginSignUpModalTreatment) {
                          setIsOpen(false);
                          setIsLoginSignUpModalOpen(true);
                        }
                      }}
                      text={<FormattedMessage defaultMessage="Log in" />}
                      variant="secondary"
                      width="full"
                    />
                  </div>
                )}
              </div>
            </FocusTrap>
          </div>
        </ModalPortal>
      )}
      {signUpModal}
      {isLoginSignUpModalTreatment && isLoginSignUpModalOpen && (
        <SignUpModal
          autoClose
          closeModal={() => setIsLoginSignUpModalOpen(false)}
          isOpen={isLoginSignUpModalOpen}
          onSuccess={() => {
            setIsLoginSignUpModalOpen(false);
            window.location.reload();
          }}
          trigger="Global Nav Menu"
          upsellTrigger="Global Nav Menu"
        />
      )}
    </>
  );
};

export default MobileMenu;
